import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import { statusLabels } from "../../lib/constants";

// ==================== VACATIONS PAGE ====================
const VacationsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'tenant_admin', 'hr_manager'].includes(user?.role);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/portal/vacations');
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching vacations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleApproveManager = async (requestId, approved) => {
    try {
      await api.put(`/portal/vacations/${requestId}/approve-manager`, { approved, notes: '' });
      toast.success(approved ? 'Aprovação da chefia registada' : 'Pedido rejeitado');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao processar aprovação');
    }
  };

  const handleApproveHR = async (requestId, approved) => {
    try {
      await api.put(`/portal/vacations/${requestId}/approve-hr`, { approved, notes: '' });
      toast.success(approved ? 'Férias aprovadas' : 'Pedido rejeitado');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao processar aprovação');
    }
  };

  return (
    <div data-testid="vacations-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Férias</h1>
          <p className="text-muted-foreground">Marcar e gerir pedidos de férias</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="create-vacation-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Novo Pedido
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : requests.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-muted-foreground">Sem pedidos de férias</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Período</th>
                <th>Dias</th>
                <th>Estado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.request_id}>
                  <td>
                    <div>
                      <p className="font-medium">{req.employee_name}</p>
                      <p className="text-sm text-muted-foreground">{req.department}</p>
                    </div>
                  </td>
                  <td>{req.start_date} a {req.end_date}</td>
                  <td className="font-semibold">{req.total_days}</td>
                  <td>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      req.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30' :
                      req.status === 'rejeitado' ? 'bg-red-100 text-red-700 dark:bg-red-900/30' :
                      req.status === 'aprovado_chefia' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                      'bg-amber-100 text-amber-700 dark:bg-amber-900/30'
                    }`}>
                      {statusLabels[req.status] || req.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedRequest(req)}
                        className="p-1.5 rounded-lg hover:bg-muted"
                        title="Ver detalhes"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      {isAdmin && req.status === 'pendente' && (
                        <>
                          <button onClick={() => handleApproveManager(req.request_id, true)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600" title="Aprovar (Chefia)">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          </button>
                          <button onClick={() => handleApproveManager(req.request_id, false)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" title="Rejeitar">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </>
                      )}
                      {isAdmin && req.status === 'aprovado_chefia' && (
                        <>
                          <button onClick={() => handleApproveHR(req.request_id, true)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600" title="Aprovar (RH)">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          </button>
                          <button onClick={() => handleApproveHR(req.request_id, false)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" title="Rejeitar">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CreateVacationModal onClose={() => setShowCreateModal(false)} onCreated={() => { setShowCreateModal(false); fetchRequests(); }} />
      )}

      {selectedRequest && (
        <VacationDetailModal request={selectedRequest} onClose={() => setSelectedRequest(null)} />
      )}
    </div>
  );
};

const CreateVacationModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({ start_date: '', end_date: '', vacation_type: 'ferias', notes: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/portal/vacations', formData);
      toast.success('Pedido de férias criado com sucesso');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Novo Pedido de Férias</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Data Início *</label>
              <input type="date" required value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" data-testid="vacation-start-date" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Data Fim *</label>
              <input type="date" required value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" data-testid="vacation-end-date" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select value={formData.vacation_type} onChange={e => setFormData({...formData, vacation_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="ferias">Férias</option>
              <option value="ferias_parciais">Férias Parciais</option>
              <option value="licenca_sem_vencimento">Licença sem Vencimento</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Notas</label>
            <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} className="w-full px-3 py-2 rounded-lg border border-input bg-background resize-none" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50" data-testid="submit-vacation-btn">{loading ? 'A criar...' : 'Criar Pedido'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const VacationDetailModal = ({ request, onClose }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div className="bg-card rounded-xl border w-full max-w-lg" onClick={e => e.stopPropagation()}>
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold">Detalhes do Pedido</h3>
        <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><p className="text-sm text-muted-foreground">Colaborador</p><p className="font-medium">{request.employee_name}</p></div>
          <div><p className="text-sm text-muted-foreground">Departamento</p><p className="font-medium">{request.department || '-'}</p></div>
          <div><p className="text-sm text-muted-foreground">Início</p><p className="font-medium">{request.start_date}</p></div>
          <div><p className="text-sm text-muted-foreground">Fim</p><p className="font-medium">{request.end_date}</p></div>
          <div><p className="text-sm text-muted-foreground">Total Dias</p><p className="font-medium text-primary">{request.total_days}</p></div>
          <div><p className="text-sm text-muted-foreground">Estado</p><span className={`px-2 py-1 rounded-full text-xs font-medium ${request.status === 'aprovado' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{statusLabels[request.status]}</span></div>
        </div>
        {request.manager_approval && (
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-sm font-medium">Aprovação Chefia</p>
            <p className="text-sm text-muted-foreground">{request.manager_approval.approved_by_name} - {request.manager_approval.date?.split('T')[0]}</p>
          </div>
        )}
        {request.hr_approval && (
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-sm font-medium">Aprovação RH</p>
            <p className="text-sm text-muted-foreground">{request.hr_approval.approved_by_name} - {request.hr_approval.date?.split('T')[0]}</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default VacationsPage;
